import {
  CameraConfigureRequest,
  CameraList,
  CameraListResponse,
  PrinterConfigureRequest,
  PrinterInfo,
  PrinterInfoCreateParams,
  PrinterInfoResponse,
  PrinterList,
  PrinterListResponse,
  RdConnectInstance,
  RdConnectInstanceIncludeOption,
  RdConnectInstanceListParams,
  RdConnectInstancePagedListResponse,
  RdConnectInstanceResponse,
  RdConnectInstanceUpdateRequest,
  StringResponse,
} from '@apiContract';

import { receivedDigitalApi } from './index';

type TakeControlParams = {
  rdConnectInstanceId?: number;
};

type CameraListParams = {
  rdConnectInstanceId?: number;
};

const DEFAULT_INCLUDE: RdConnectInstanceIncludeOption[] = [
  RdConnectInstanceIncludeOption.ConnectedUser,
  RdConnectInstanceIncludeOption.CreatedBy,
];

interface PrinterConfigureRequestParams extends PrinterConfigureRequest {
  id: number | string;
}

interface CameraConfigureRequestParams extends CameraConfigureRequest {
  id: number | string;
}

export const connectInstanceApi = receivedDigitalApi.injectEndpoints({
  endpoints: (builder) => ({
    getInstances: builder.query<RdConnectInstance[], RdConnectInstanceListParams | void>({
      query: (params) => ({
        url: 'RdConnectInstance',
        params: {
          Include: DEFAULT_INCLUDE,
          ...params,
        },
      }),
      providesTags: ['ConnectInstance'],
      transformResponse: (response: RdConnectInstancePagedListResponse): RdConnectInstance[] =>
        response.objectData.items || [],
    }),
    getPrinterList: builder.query<PrinterList, number | string>({
      query: (connectionId) => `RdConnectOperations/printer-list/${connectionId}`,
      transformResponse: (response: PrinterListResponse): PrinterList => response.objectData,
    }),
    checkPrinterStatus: builder.query<PrinterInfo, PrinterInfoCreateParams>({
      query: (params) => `RdConnectOperations/printer-info/${params.connectionId}/${params.name}`,
      transformResponse: (response: PrinterInfoResponse): PrinterInfo => response.objectData,
    }),
    takeControl: builder.mutation<StringResponse, TakeControlParams>({
      query: (requestData) => ({
        url: `RdConnectInstance/take/${requestData.rdConnectInstanceId}`,
        method: 'GET',
      }),
      invalidatesTags: ['ConnectInstance'],
    }),
    createInstallation: builder.mutation<string, { name: string }>({
      query: ({ name }) => ({
        url: `RdConnectInstance/create-activation/${encodeURIComponent(name)}`,
        method: 'POST',
      }),
      transformResponse: (response: StringResponse): string => response.objectData,
      invalidatesTags: ['ConnectInstance'],
    }),
    updateInstallation: builder.mutation<RdConnectInstance, { id: number } & RdConnectInstanceUpdateRequest>({
      query: ({ id, ...body }) => ({
        url: `RdConnectInstance/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: RdConnectInstanceResponse): RdConnectInstance => response.objectData,
      invalidatesTags: ['ConnectInstance'],
    }),
    getCamerasList: builder.query<CameraList, CameraListParams>({
      query: (requestData) => ({
        url: `RdConnectOperations/camera-list/${requestData.rdConnectInstanceId}`,
      }),
      transformResponse: (response: CameraListResponse): CameraList => response.objectData,
      providesTags: ['CameraList'],
    }),
    deleteInstallation: builder.mutation<void, number>({
      query: (id) => ({
        url: `RdConnectInstance/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConnectInstance'],
    }),
    setDefaultPrinter: builder.mutation<void, PrinterConfigureRequestParams>({
      query: ({ id, ...body }) => ({
        url: `RdConnectOperations/printer-configure/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ConnectInstance'],
    }),
    setDefaultCamera: builder.mutation<void, CameraConfigureRequestParams>({
      query: ({ id, ...body }) => ({
        url: `RdConnectOperations/camera-configure/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CameraList'],
    }),
  }),
});

export const {
  useLazyGetCamerasListQuery,
  useGetInstancesQuery,
  useTakeControlMutation,
  useGetCamerasListQuery,
  useGetPrinterListQuery,
  useCheckPrinterStatusQuery,
  useCreateInstallationMutation,
  useUpdateInstallationMutation,
  useDeleteInstallationMutation,
  useSetDefaultPrinterMutation,
  useSetDefaultCameraMutation,
} = connectInstanceApi;
